import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, apiUrl, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { CallData } from '@/types/api';

type CallFilters = {
  /** 'deleted' is the trash: a mode, never mixed into the ordinary list. */
  filter?: 'voicemail' | 'blocked' | 'known' | 'unknown' | 'screened' | 'deleted';
  page?: number;
};

export function useCalls(filters: CallFilters = {}) {
  return useQuery({
    queryKey: ['calls', filters],
    queryFn: async (): Promise<Paginated<CallData>> => {
      const { data } = await api.get<Paginated<CallData>>('/v1/calls', { params: filters });
      return data;
    },
    refetchInterval: 60_000,
  });
}

/** Absolute URL to the proxied voicemail recording, for an <audio> element. */
export function recordingUrl(callId: number): string {
  return apiUrl(`/v1/calls/${callId}/recording`);
}

export function useMarkCallRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.post(`/v1/calls/${id}/read`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calls'] }),
  });
}

/** Soft delete. The voicemail stays on Twilio until the trash is emptied. */
export function useDeleteCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/calls/${id}`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calls'] }),
  });
}

export function useRestoreCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.post(`/v1/calls/${id}/restore`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calls'] }),
  });
}

/**
 * Empties the trash for good and asks Twilio to delete the voicemail audio
 * behind each row. Takes the same filters as the list so it never reaches past
 * what is on screen.
 */
export function usePurgeCalls() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (filters: CallFilters = {}): Promise<number> => {
      await primeCsrf();
      const { data } = await api.delete<{ purged: number }>('/v1/calls/trashed', {
        params: { ...filters, page: undefined },
      });
      return data.purged;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['calls'] }),
  });
}
